import {
  cargoSubtype,
  EntityIdentifier,
  FreightTypeEntity,
  getObjectId,
  Quantity,
  TimeGroup,
  toTimeGroup,
  TransportCategory,
  transportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Group, Modal, ModalProps, NumberInput} from "@mantine/core";
import {useCallback} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {TimeGroupSelect} from "../../select/entity/TimeGroupSelect/TimeGroupSelect";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {EntitySelect} from "../../select/entity/EntitySelect/EntitySelect";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";

interface ProductionVolumeFormProps {
  options?: ProductionVolumeFormOptions;
  defaultValues?: Partial<ProductionVolumeFormValues>;
  onSubmit?: (values: ProductionVolumeFormValues) => void;
  submitting?: boolean;
}

export interface ProductionVolumeFormOptions {
  // props here
  regionId?: string
  category?: TransportCategory
}

export interface ProductionVolumeFormValues {
  timeGroup: TimeGroup
  date: Date
  object: EntityIdentifier
  quantity: Quantity;
  freightType: FreightTypeEntity | string;
}

const getDefaultValues = (defaultValues: Partial<ProductionVolumeFormValues> | undefined) => {
  return {
    timeGroup: defaultValues?.timeGroup?.code,
    date: defaultValues?.date,
    object: {
      id: defaultValues?.object?.id,
      type: defaultValues?.object?.type,
    },
    quantity: defaultValues?.quantity?.number,
    quantityUom: defaultValues?.quantity?.units,
    freightType: getObjectId(defaultValues?.freightType)
  }
}

export const ProductionVolumeForm = (props: ProductionVolumeFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        timeGroup: toTimeGroup(values.timeGroup!),
        date: values.date!,
        object: {
          id: values.object.id!,
          type: values.object.type!,
        },
        quantity: {
          number: values.quantity!,
          units: values.quantityUom!
        },
        freightType: values.freightType!,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <TimeGroupSelect
        label={t("common.entity.productionvolume.form.field.timegroup", "Time Period")}
        placeholder={t<string>("common.entity.productionvolume.form.field.timegroup.placeholder", "Time Period")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("timeGroup")} />
      <DateInput
        valueFormat="MM/DD/YYYY"
        label={t("common.entity.productionvolume.form.field.date", "Date")}
        placeholder={t<string>("common.entity.productionvolume.form.field.date.placeholder", "Date")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("date")} />
      <Divider my="sm" variant="dotted"/>
      <EntityTypeSelect
        options={['WELL']}
        label={t("common.entity.productionvolume.form.field.object", "Object")}
        placeholder={t<string>("common.entity.productionvolume.form.field.object.placeholder", "Object")}
        withAsterisk
        required
        clearable
        {...form.getInputProps("object.type")} />
      <EntitySelect
        pt="md"
        entityType={form.getTransformedValues().object?.type}
        regionId={options?.regionId}
        withAsterisk
        required
        clearable
        {...form.getInputProps("object.id")} />
      <Divider my="sm" variant="dotted"/>
      <Group grow>
        <NumberInput
          label={t("common.entity.productionvolume.form.field.quantity", "Quantity")}
          placeholder={t<string>("common.entity.productionvolume.form.field.quantity.placeholder", "Quantity")}
          precision={5}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          withAsterisk
          required
          {...form.getInputProps("quantity")} />
        <UnitOfMeasureSelect
          label={t("common.entity.productionvolume.form.field.uom", "Units")}
          placeholder={t<string>("common.entity.productionvolume.form.field.uom.placeholder", "Units")}
          clearable
          withAsterisk
          required
          {...form.getInputProps("quantityUom")} />
      </Group>
      <FreightTypeSelect
        label={t("common.entity.productionvolume.form.field.freighttype", "Freight Type")}
        placeholder={t<string>("common.entity.productionvolume.form.field.freighttype.placeholder", "Freight Type")}
        selector={{
          regionId: options?.regionId,
          cargoSubtype: options?.category === transportCategory.oil ? cargoSubtype.oil : undefined
        }}
        clearable
        withAsterisk
        required
        {...form.getInputProps("freightType")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface ProductionVolumeFormModalProps extends ProductionVolumeFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ProductionVolumeFormModal = (props: ProductionVolumeFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ProductionVolumeForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
